import { useCallback, useId, useRef, useState, useSyncExternalStore, type ReactNode, type Ref } from 'react';
import { Popover } from './Popover';

/**
 * How the page looks — theme and motion — and the one place both are
 * read and written (4.1.56 refactored this from a <details> control).
 *
 * The preference is the visitor's, kept in their browser: `booking-theme`
 * ('light' | 'dark'; anything else means "not chosen") and
 * `booking-reduce-motion`. It is not an account setting and never reaches
 * the server. Until a choice is made the page follows the operating
 * system, live: a change there re-applies without a reload.
 *
 * ONE STORE, SEVERAL CONTROLS. A screen can carry the control in more
 * than one place (the console has it in the sidebar, and a page may add
 * another), so the state lives at module level and every control reads
 * it through `useAppearance`; two controls on one page can never
 * disagree about which theme is on.
 */
export type Theme = 'light' | 'dark' | 'system';
type Resolved = 'light' | 'dark';

interface AppearanceState { theme: Theme; motion: boolean; resolved: Resolved }

function storedTheme(): Theme { try { const t = localStorage.getItem('booking-theme'); return t === 'light' || t === 'dark' ? t : 'system'; } catch { return 'system'; } }
function storedMotion() { try { return localStorage.getItem('booking-reduce-motion') === 'true'; } catch { return false; } }
function systemDark() { return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches; }
function resolve(theme: Theme): Resolved { return theme === 'system' ? (systemDark() ? 'dark' : 'light') : theme; }

let state: AppearanceState = { theme: 'system', motion: false, resolved: 'light' };
const listeners = new Set<() => void>();
let watching = false;

function apply() {
  document.documentElement.dataset.theme = state.resolved;
  document.documentElement.dataset.reduceMotion = String(state.motion);
}
function emit() { for (const l of listeners) l(); }
function watchSystem() {
  if (watching || typeof matchMedia !== 'function') return;
  watching = true;
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (state.theme !== 'system') return;
    state = { ...state, resolved: resolve('system') };
    apply();
    emit();
  });
}

/** Apply saved preferences before any route renders, including recovery and handoff pages. */
export function initializeAppearance() {
  const theme = storedTheme();
  state = { theme, motion: storedMotion(), resolved: resolve(theme) };
  apply();
  watchSystem();
}

export function setTheme(theme: Theme) {
  state = { ...state, theme, resolved: resolve(theme) };
  try { localStorage.setItem('booking-theme', theme); } catch {}
  apply();
  watchSystem();
  emit();
}

export function setReduceMotion(motion: boolean) {
  state = { ...state, motion };
  try { localStorage.setItem('booking-reduce-motion', String(motion)); } catch {}
  apply();
  emit();
}

function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }
function snapshot() { return state; }

/** The current preference, live, and the two ways to change it. */
export function useAppearance() {
  const s = useSyncExternalStore(subscribe, snapshot, snapshot);
  return { theme: s.theme, motion: s.motion, resolved: s.resolved, setTheme, setReduceMotion };
}

/**
 * The words on this control, so a translated page can pass its own. They
 * default to English, which is what the owner's console (untranslated by
 * design) wants; the public booking pages pass the reader's language.
 */
export interface AppearanceLabels { appearance: string; theme: string; light: string; dark: string; reduceMotion: string; useDevice: string; followingDevice: string }
const ENGLISH: AppearanceLabels = { appearance: 'Appearance', theme: 'Theme', light: 'Light', dark: 'Dark', reduceMotion: 'Reduce motion', useDevice: 'Use device setting', followingDevice: 'Following your device setting' };

/* ---- Glyphs: line icons on currentColor, like IndustryIcon and the language switcher's globe. ---- */
export function SunGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2.5M12 19v2.5M2.5 12H5M19 12h2.5M5.3 5.3l1.8 1.8M16.9 16.9l1.8 1.8M5.3 18.7l1.8-1.8M16.9 7.1l1.8-1.8" />
    </svg>
  );
}
export function MoonGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a7 7 0 1 0 10.5 10.5Z" />
    </svg>
  );
}
function ChevronGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/**
 * The panel itself: a segmented Theme control (both choices in view,
 * the one on screen marked) and a Reduce motion switch that says
 * whether it is on. It is used inside the popover and inside the
 * sidebar's disclosure, so the arrangement is the same everywhere.
 *
 * TWO CHOICES, NOT THREE — owner's decision, 2026-09-18. There is no
 * "System" option to pick: until a person chooses, the page simply
 * follows the operating system (and keeps following it live), and the
 * control shows whichever of Light or Dark that currently is. Choosing
 * one makes it stick in this browser. A stored 'system' from before
 * this change still reads as "not chosen yet".
 */
export function AppearancePanel({ labels = ENGLISH, title = true }: { labels?: AppearanceLabels; title?: boolean }) {
  const { theme, resolved, motion, setTheme, setReduceMotion } = useAppearance();
  const themeId = useId();
  const options: { value: Resolved; label: string; glyph: ReactNode }[] = [
    { value: 'light', label: labels.light, glyph: <SunGlyph /> },
    { value: 'dark', label: labels.dark, glyph: <MoonGlyph /> },
  ];
  const group = useRef<HTMLDivElement>(null);
  // Roving tabindex: the chosen option is the one Tab lands on; the
  // arrows move the choice, as they do in any radio group.
  const onKey = (e: React.KeyboardEvent, at: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (at + 1) % options.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (at - 1 + options.length) % options.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = options.length - 1;
    if (next === null) return;
    e.preventDefault();
    e.stopPropagation();
    setTheme(options[next]!.value);
    group.current?.querySelectorAll<HTMLButtonElement>('[role=radio]')[next]?.focus();
  };
  return (
    <div className="appearance-panel">
      {title && <p className="ui-popover__title">{labels.appearance}</p>}
      <div className="appearance-field">
        <span className="appearance-field__label" id={themeId}>{labels.theme}</span>
        <div ref={group} className="segmented" role="radiogroup" aria-labelledby={themeId}>
          {options.map((o, i) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={resolved === o.value}
              tabIndex={resolved === o.value ? 0 : -1}
              data-autofocus={resolved === o.value ? '' : undefined}
              className="segmented__option"
              onClick={() => setTheme(o.value)}
              onKeyDown={(e) => onKey(e, i)}
            >
              {o.glyph}
              <span>{o.label}</span>
            </button>
          ))}
        </div>
        {/* The way back to following the device, once a choice has been
            made — the one thing a two-way control cannot say by itself.
            While nothing is chosen it says so instead, quietly. */}
        {theme === 'system'
          ? <span className="appearance-reset appearance-reset--note">{labels.followingDevice}</span>
          : <button type="button" className="appearance-reset" onClick={() => setTheme('system')}>{labels.useDevice}</button>}
      </div>
      <div className="switch">
        <label className="switch__control">
          <input type="checkbox" checked={motion} onChange={(e) => setReduceMotion(e.target.checked)} />
          <span className="switch__track" aria-hidden="true" />
          <span className="switch__text">{labels.reduceMotion}</span>
        </label>
        <span className="switch__state" aria-hidden="true">{motion ? 'On' : 'Off'}</span>
      </div>
    </div>
  );
}

/**
 * The compact trigger: a sun or a moon for whichever is on screen right
 * now (so under System it follows the operating system). Icon only by
 * default; `showLabel` adds the word beside it for a footer or a form,
 * where a lone glyph has nothing to lean on.
 */
export function AppearanceButton({ open, onClick, label = ENGLISH.appearance, showLabel = false, controls, size, ref }: {
  open: boolean;
  onClick: () => void;
  label?: string;
  showLabel?: boolean;
  controls?: string;
  size?: 'sm';
  ref?: Ref<HTMLButtonElement>;
}) {
  const { resolved } = useAppearance();
  return (
    <button
      ref={ref}
      type="button"
      className={`ui-icon-btn${size === 'sm' ? ' ui-ui-icon-btn--sm' : ''}${showLabel ? ' ui-ui-icon-btn--labelled' : ''} appearance-button`}
      aria-label={showLabel ? undefined : label}
      title={showLabel ? undefined : label}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-controls={open ? controls : undefined}
      onClick={onClick}
    >
      {resolved === 'dark' ? <MoonGlyph /> : <SunGlyph />}
      {showLabel && <span>{label}</span>}
    </button>
  );
}

/** The panel, anchored to a button somebody else renders. */
export function AppearancePopover({ open, onClose, anchorRef, labels = ENGLISH, placement, id }: {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  labels?: AppearanceLabels;
  placement?: 'top' | 'bottom';
  id?: string;
}) {
  return (
    <Popover open={open} onClose={onClose} anchorRef={anchorRef} placement={placement} label={labels.appearance} id={id} className="ui-popover--appearance">
      <AppearancePanel labels={labels} />
    </Popover>
  );
}

/**
 * Button and popover together — what a header, a footer or a form
 * drops in. `placement="top"` for a footer, where "below" is off the
 * bottom of the page.
 */
export function Appearance({ labels = ENGLISH, placement = 'bottom', showLabel = false, size }: {
  labels?: AppearanceLabels;
  placement?: 'top' | 'bottom';
  showLabel?: boolean;
  size?: 'sm';
} = {}) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const id = useId();
  const close = useCallback(() => setOpen(false), []);
  return (
    <>
      <AppearanceButton ref={button} open={open} onClick={() => setOpen((o) => !o)} label={labels.appearance} showLabel={showLabel} controls={id} size={size} />
      <AppearancePopover open={open} onClose={close} anchorRef={button} labels={labels} placement={placement} id={id} />
    </>
  );
}

/**
 * The sidebar's version: the same panel, expanded IN THE SIDEBAR under
 * its own row rather than floating over the page. The sidebar scrolls
 * and folds away on a phone; a panel that lives inside it goes wherever
 * it goes, and never covers a link.
 */
export function AppearanceDisclosure({ labels = ENGLISH }: { labels?: AppearanceLabels } = {}) {
  const [open, setOpen] = useState(false);
  const { resolved } = useAppearance();
  const id = useId();
  return (
    <div className="appearance-disclosure">
      <button type="button" className="appearance-disclosure__toggle" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
        <span className="appearance-disclosure__glyph">{resolved === 'dark' ? <MoonGlyph /> : <SunGlyph />}</span>
        <span>{labels.appearance}</span>
        <span className="appearance-disclosure__chevron"><ChevronGlyph /></span>
      </button>
      {open && (
        <div id={id} className="appearance-disclosure__panel">
          <AppearancePanel labels={labels} title={false} />
        </div>
      )}
    </div>
  );
}
